import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, X, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Link } from "react-router-dom";

interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
  personalization: boolean;
}

const COOKIE_CONSENT_KEY = "cookie_consent";
const COOKIE_PREFERENCES_KEY = "cookie_preferences";

const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    essential: true, // Always required
    analytics: false,
    marketing: false,
    personalization: false,
  });

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Delay showing banner slightly for better UX
      const timer = setTimeout(() => setIsVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const savePreferences = (prefs: CookiePreferences) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, "true");
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(prefs));
    setIsVisible(false);
  };

  const handleAcceptAll = () => {
    const allAccepted: CookiePreferences = {
      essential: true,
      analytics: true,
      marketing: true,
      personalization: true,
    };
    savePreferences(allAccepted);
  };

  const handleAcceptSelected = () => {
    savePreferences(preferences);
  };

  const handleRejectAll = () => {
    const onlyEssential: CookiePreferences = {
      essential: true,
      analytics: false,
      marketing: false,
      personalization: false,
    };
    savePreferences(onlyEssential);
  };

  const cookieTypes = [
    {
      key: "essential" as const,
      label: "Essential",
      description: "Required for the website to function. Cannot be disabled.",
      disabled: true,
    },
    {
      key: "analytics" as const,
      label: "Analytics",
      description: "Help us understand how visitors interact with our website.",
      disabled: false,
    },
    {
      key: "marketing" as const,
      label: "Marketing",
      description: "Used to deliver personalized advertisements.",
      disabled: false,
    },
    {
      key: "personalization" as const,
      label: "Personalization",
      description: "Remember your preferences and enhance your experience.",
      disabled: false,
    },
  ];

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6"
        >
          <div className="max-w-4xl mx-auto bg-card border border-border rounded-2xl shadow-2xl overflow-hidden">
            {/* Main Banner */}
            <div className="p-4 md:p-6">
              <div className="flex items-start gap-4">
                <div className="hidden sm:flex w-12 h-12 rounded-full bg-primary/10 items-center justify-center flex-shrink-0">
                  <Cookie className="w-6 h-6 text-primary" />
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="text-lg font-semibold text-foreground mb-1">
                        We value your privacy 🍪
                      </h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. 
                        You can choose which cookies you'd like to accept.{" "}
                        <Link to="/cookie-policy" className="text-primary hover:underline">
                          Learn more
                        </Link>
                      </p>
                    </div>
                    
                    <button
                      onClick={handleRejectAll}
                      className="p-2 hover:bg-muted rounded-full transition-colors flex-shrink-0"
                      aria-label="Close and reject non-essential cookies"
                    >
                      <X className="w-5 h-5 text-muted-foreground" />
                    </button>
                  </div>

                  {/* Cookie Details Toggle */}
                  <button
                    onClick={() => setShowDetails(!showDetails)}
                    className="flex items-center gap-1 text-sm text-primary hover:underline mt-3"
                  >
                    {showDetails ? "Hide" : "Show"} cookie preferences
                    {showDetails ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </button>

                  {/* Expandable Cookie Options */}
                  <AnimatePresence>
                    {showDetails && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="grid gap-3 mt-4 pt-4 border-t border-border">
                          {cookieTypes.map((cookie) => (
                            <div
                              key={cookie.key}
                              className="flex items-center justify-between gap-4 p-3 rounded-lg bg-muted/50"
                            >
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-foreground">
                                  {cookie.label}
                                  {cookie.disabled && (
                                    <span className="ml-2 text-xs text-muted-foreground">(Required)</span>
                                  )}
                                </p>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                  {cookie.description}
                                </p>
                              </div>
                              <Switch
                                checked={preferences[cookie.key]}
                                onCheckedChange={(checked) =>
                                  setPreferences((prev) => ({
                                    ...prev,
                                    [cookie.key]: checked,
                                  }))
                                }
                                disabled={cookie.disabled}
                                aria-label={`${cookie.label} cookies`}
                              />
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-3 mt-4">
                    <Button
                      onClick={handleAcceptAll}
                      className="bg-primary hover:bg-primary/90"
                    >
                      Accept All
                    </Button>
                    {showDetails && (
                      <Button
                        onClick={handleAcceptSelected}
                        variant="outline"
                      >
                        Save Preferences
                      </Button>
                    )}
                    <Button
                      onClick={handleRejectAll}
                      variant="ghost"
                      className="text-muted-foreground"
                    >
                      Reject Non-Essential
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieBanner;
